import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import { Link } from 'react-router-dom';

interface PickupSectionProps {
  category: 'artists' | 'events' | 'nodes' | 'market';
}

interface PickupItem {
  id: string;
  name: string;
  image?: string;
  adminComment?: string;
  genre?: string;
  location?: string;
  price?: number;
}

export function PickupSection({ category }: PickupSectionProps) {
  const [items, setItems] = useState<PickupItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPickupItems();
  }, [category]);

  const fetchPickupItems = async () => {
    try {
      setLoading(true);

      // pickup_itemsテーブルから該当カテゴリーのアイテムを取得
      const { data: pickupData } = await supabase
        .from('pickup_items')
        .select('item_id, item_type, admin_comment')
        .eq('category', category)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (!pickupData || pickupData.length === 0) {
        setItems([]);
        setLoading(false);
        return;
      }

      // 各アイテムの詳細情報を取得
      const itemsWithDetails = await Promise.all(
        pickupData.map(async (pickup) => {
          let itemData = null;

          if (category === 'artists') {
            const { data } = await supabase
              .from('artist_layers')
              .select('id, artist_name, profile_image, artist_main_genres(genre_name)')
              .eq('id', pickup.item_id)
              .single();
            itemData = data;

            if (itemData) {
              return {
                id: itemData.id,
                name: itemData.artist_name || 'Unknown Artist',
                image: itemData.profile_image || `https://public.readdy.ai/ai/img_res/6ef2d4d29fe65b4e10f5f2133bf53407.jpg${itemData.id}&orientation=squarish`,
                adminComment: pickup.admin_comment,
                genre: (itemData as any).artist_main_genres?.[0]?.genre_name
              };
            }

          } else if (category === 'events') {
            const { data } = await supabase
              .from('events')
              .select('id, title, image_url, prefecture, event_main_genres(genre_name)')
              .eq('id', pickup.item_id)
              .single();
            itemData = data;

            if (itemData) {
              return {
                id: itemData.id,
                name: itemData.title || 'Unknown Event',
                image: itemData.image_url || `https://readdy.ai/api/search-image?query=cyberpunk%20music%20event%20concert%20stage%20neon%20lights%20futuristic%20atmosphere&width=600&height=400&seq=event-pickup-${itemData.id}&orientation=landscape`,
                adminComment: pickup.admin_comment,
                genre: (itemData as any).event_main_genres?.[0]?.genre_name,
                location: itemData.prefecture
              };
            }

          } else if (category === 'nodes') {
            const { data } = await supabase
              .from('shop_layers')
              .select('id, shop_name, shop_image, prefecture, shop_main_genres(genre_name)')
              .eq('id', pickup.item_id)
              .eq('is_published', true)
              .single();
            itemData = data;

            if (itemData) {
              return {
                id: itemData.id,
                name: itemData.shop_name || 'Unknown Node',
                image: itemData.shop_image || `https://readdy.ai/api/search-image?query=cyberpunk%20music%20venue%20bar%20club%20neon%20interior%20futuristic%20atmosphere&width=600&height=400&seq=shop-pickup-${itemData.id}&orientation=landscape`,
                adminComment: pickup.admin_comment,
                genre: (itemData as any).shop_main_genres?.[0]?.genre_name,
                location: itemData.prefecture
              };
            }

          } else if (category === 'market') {
            const { data } = await supabase
              .from('merchandise')
              .select('id, name, image_url, price')
              .eq('id', pickup.item_id)
              .eq('status', 'available')
              .single();
            itemData = data;

            if (itemData) {
              return {
                id: itemData.id,
                name: itemData.name || 'Unknown Item',
                image: itemData.image_url || `https://readdy.ai/api/search-image?query=cyberpunk%20merchandise%20product%20neon%20glow%20minimal%20background%20futuristic&width=400&height=400&seq=merch-pickup-${itemData.id}&orientation=squarish`,
                adminComment: pickup.admin_comment,
                price: itemData.price
              };
            }
          }

          return null;
        })
      );

      // nullを除外
      const validItems = itemsWithDetails.filter((item): item is PickupItem => item !== null);
      setItems(validItems);

    } catch (error) {
      console.error('Error fetching pickup items:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryPath = (itemId: string) => {
    switch (category) {
      case 'artists': return `/artist/${itemId}`;
      case 'events': return `/event/${itemId}`;
      case 'nodes': return `/shop/${itemId}`;
      case 'market': return `/merchandise`;
      default: return '#';
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-zinc-800/50 rounded-2xl h-80 animate-pulse" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((item) => (
          <Link
            key={item.id}
            to={getCategoryPath(item.id)}
            className="group rounded-2xl overflow-hidden transition-all duration-200 cursor-pointer relative bg-zinc-800/40 border border-zinc-700/50 hover:bg-zinc-700/50 hover:border-amber-500/30"
            style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}
          >
            {/* PICK UPバッジ */}
            <div className="absolute top-3 right-3 z-10 flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400/20 border border-amber-400/40 backdrop-blur-sm">
              <i className="ri-star-fill text-amber-400 text-[10px]" />
              <span className="text-amber-300 text-[10px] font-bold tracking-widest">PICK UP</span>
            </div>

            <div className="aspect-video overflow-hidden">
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
            </div>

            <div className="p-4">
              <h4 className="text-base font-bold text-zinc-100 group-hover:text-amber-300 transition-colors mb-3">
                {item.name}
              </h4>

              {item.adminComment && (
                <div className="rounded-xl p-3 mb-4 bg-zinc-700/40 border border-zinc-600/40">
                  <p className="text-xs text-zinc-300 leading-relaxed">
                    <i className="ri-chat-quote-line text-amber-400 mr-1.5" />
                    {item.adminComment}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  {item.genre && (
                    <p className="text-xs text-zinc-500">{item.genre}</p>
                  )}
                  {item.location && (
                    <p className="text-xs text-zinc-600 mt-1">
                      <i className="ri-map-pin-line mr-1" />
                      {item.location}
                    </p>
                  )}
                  {item.price !== undefined && (
                    <p className="text-sm text-amber-300 font-bold mt-2">¥{item.price.toLocaleString()}</p>
                  )}
                </div>
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-zinc-700 border border-zinc-600 group-hover:translate-x-1 transition-transform duration-200">
                  <i className="ri-arrow-right-line text-zinc-300 text-sm" />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}